import React from 'react';
import { useTranslation } from 'react-i18next';

// minute 0=winter, 15=spring, 30=summer, 45=autumn
// hand at top (12 o'clock) = minute 0 = winter
const SEASONS = [
  { key: 'winter', i18nKey: 'WINTER', emoji: '❄️', minute: 0,  color: '#7ec8e3' },
  { key: 'spring', i18nKey: 'SPRING', emoji: '🌸', minute: 15, color: '#a8e6a3' },
  { key: 'summer', i18nKey: 'SUMMER', emoji: '☀️', minute: 30, color: '#ffd966' },
  { key: 'autumn', i18nKey: 'AUTOMN', emoji: '🍂', minute: 45, color: '#e8a87c' },
];

// minute 0 → -90° (top), clockwise
const minuteToAngleDeg = (m) => (m / 60) * 360 - 90;

const polarToXY = (cx, cy, angleDeg, radius) => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
};

export default function SeasonClock({ minutes = 0, lang = 0 }) {
  const { t, i18n } = useTranslation();
  const label = (s) => i18n.getFixedT(String(lang))(s.i18nKey);
  const size   = 200;
  const cx     = size / 2;
  const cy     = size / 2;
  const outerR = 80;
  const innerR = 54;
  const handR  = 44;
  const dotR   = 7;

  const m = ((minutes % 60) + 60) % 60;

  // find the two surrounding seasons and interpolate between them
  const prevSeason = SEASONS.reduce((best, s) => {
    const diff = (m - s.minute + 60) % 60;
    const bestDiff = (m - best.minute + 60) % 60;
    return diff < bestDiff ? s : best;
  });
  const nextIdx = (SEASONS.indexOf(prevSeason) + 1) % SEASONS.length;
  const nextSeason = SEASONS[nextIdx];
  const gap = (nextSeason.minute - prevSeason.minute + 60) % 60; // always 15
  const elapsed = (m - prevSeason.minute + 60) % 60;
  const nextPct = Math.round((elapsed / gap) * 100);
  const prevPct = 100 - nextPct;
  const isMixed = nextPct > 0 && prevPct > 0;

  // center dot color: blend between the two seasons
  const dotColor = isMixed ? nextSeason.color : prevSeason.color;

  const handAngle = minuteToAngleDeg(m);
  const handTip   = polarToXY(cx, cy, handAngle, handR);

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>

        {/* 4 background quadrants, each centered on a season point */}
        {SEASONS.map((s) => {
          const startDeg = minuteToAngleDeg(s.minute - 7.5);
          const endDeg   = minuteToAngleDeg(s.minute + 7.5);
          const o1 = polarToXY(cx, cy, startDeg, outerR);
          const o2 = polarToXY(cx, cy, endDeg,   outerR);
          const i1 = polarToXY(cx, cy, startDeg, innerR);
          const i2 = polarToXY(cx, cy, endDeg,   innerR);
          const isActive = s.key === prevSeason.key || (isMixed && s.key === nextSeason.key);
          return (
            <path
              key={s.key}
              d={`M ${o1.x} ${o1.y} A ${outerR} ${outerR} 0 0 1 ${o2.x} ${o2.y}
                  L ${i2.x} ${i2.y} A ${innerR} ${innerR} 0 0 0 ${i1.x} ${i1.y} Z`}
              fill={s.color}
              opacity={isActive ? 1 : 0.4}
              stroke="white"
              strokeWidth={1}
            />
          );
        })}

        {/* emoji at each season point */}
        {SEASONS.map((s) => {
          const pos = polarToXY(cx, cy, minuteToAngleDeg(s.minute), (outerR + innerR) / 2);
          return (
            <text key={s.key}
              x={pos.x} y={pos.y}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={18} style={{ userSelect: 'none' }}>
              {s.emoji}
            </text>
          );
        })}

        {/* clock hand */}
        <line
          x1={cx} y1={cy} x2={handTip.x} y2={handTip.y}
          stroke="#222" strokeWidth={3} strokeLinecap="round"
        />

        {/* center dot */}
        <circle cx={cx} cy={cy} r={dotR}
          fill={dotColor} stroke="#333" strokeWidth={2} />

      </svg>

      <div style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#333' }}>
        {isMixed
          ? <>{prevSeason.emoji} {label(prevSeason)} {prevPct}% / {nextSeason.emoji} {label(nextSeason)} {nextPct}%</>
          : <>{prevSeason.emoji} {label(prevSeason)}</>}
      </div>
    </div>
  );
}
